import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Skeleton,
  Chip,
  Autocomplete,
  IconButton,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetAllThingsQuery } from '../../redux/thunks/things/thingsget';
import { FORM_CONFIG } from './BusinessFormConstants';


const CategoriesServicesForm = ({ formData, onChange, isLoading }) => {
  const token = useSelector((state) => state.auth?.token || null);
  const spId = useSelector((state) => state.auth?.user?.spId);  

  const args = {
    spId: spId,
    authType: 'token',
    token: token
  };
  
  const { data: categoriesData, isLoading: isCategoriesDataLoading } = useGetAllThingsQuery(args);

  // Handler for platform changes
  const handlePlatformChange = (index, field, value) => {
    const platforms = [...(formData.platforms || [])];
    if (!platforms[index]) {
      platforms[index] = {};
    }
    platforms[index][field] = value;

    onChange({
      target: {
        name: 'platforms',
        value: platforms
      }
    });
  };

  const handleCategoryChange = (_, value) => {
    onChange({
      target: {
        name: 'additionalCategories',
        value: value
      }
    });
  };

  // Handler to remove a platform
  const handleRemovePlatform = (indexToRemove) => {
    const platforms = (formData.platforms || [])
      .filter((_, index) => index !== indexToRemove);

    onChange({
      target: {
        name: 'platforms',
        value: platforms
      }
    });
  };

  if (isLoading || isCategoriesDataLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          <Skeleton variant="text" width="30%" />
        </Typography>
        <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={32} sx={{ mt: 4, mb: 2 }} />
        {[1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Categories Section */}
      <Typography variant="h6" gutterBottom>
        Categories
      </Typography>
      
      <Box sx={{ mt: 3, mb: 5 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select additional categories (Optional)
        </Typography>
        <Autocomplete
          multiple
          options={categoriesData?.data || []}
          value={formData.additionalCategories || []}
          getOptionLabel={(option) => (option ? option.name || '' : '')}
          onChange={handleCategoryChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search and select categories that best describe your business"
              helperText="Optional - Select multiple categories to help customers find you more easily"
              fullWidth
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...chipProps}
                  label={option?.name}
                  color="primary"
                  variant="outlined"
                />
              );
            })
          }
        />
      </Box>

      {/* Platforms Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Other Platforms (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Add other platforms where your business can be found - helps customers discover you across different channels
        </Typography>

        {/* Platform Input Fields */}
        <Box sx={{ mt: 2 }}>
        {Array.from({ length: FORM_CONFIG.maxPlatforms }).map((_, index) => {
            const platforms = formData.platforms || [];
            const platform = platforms[index] || {};

            // Show field if:
            // - it's the first one
            // - OR the current platform has data
            // - OR there's a platform before it with data
            const shouldShow = 
              index === 0 || 
              platform.name || 
              platform.link || 
              (index > 0 && platforms[index - 1] && (platforms[index - 1].name || platforms[index - 1].link));

            if (!shouldShow) return null;

            return (
              <Paper
                key={index}
                variant="outlined"
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    color="primary.main" 
                    sx={{ fontWeight: 600 }}
                  >
                    Platform {index + 1}
                  </Typography>
                  {(platform.name || platform.link) && (
                    <IconButton 
                      onClick={() => handleRemovePlatform(index)}
                      color="error"
                      size="small"
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Platform Name"
                      placeholder="e.g., Yelp, Google My Business, Thumbtack"
                      value={platform.name || ''}
                      onChange={(e) => handlePlatformChange(index, 'name', e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Platform URL"
                      placeholder="https://www.example.com/your-business"
                      value={platform.link || ''}
                      onChange={(e) => handlePlatformChange(index, 'link', e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Box>

        {/* Show helpful message */}
        {(!formData.platforms || formData.platforms.length === 0) && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: 'info.light', 
              borderColor: 'info.main',
              borderStyle: 'dashed'
            }}
          >
            <Typography variant="body2" color="info.dark" sx={{ textAlign: 'center' }}>
              💡 Add platforms like Yelp, Google My Business, or industry-specific directories to increase your online presence
            </Typography>
          </Paper>
        )}

        {/* Show message when max platforms reached */}
        {formData.platforms?.length === FORM_CONFIG.maxPlatforms && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontWeight: 500 }}>
            ⚠️ Maximum number of platforms reached (${FORM_CONFIG.maxPlatforms})
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CategoriesServicesForm;